import { useState } from 'react';
import { useShopAssetsSummary } from '@/hooks/useAssetDowntime';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronUp, Package, PowerOff, Wrench, Clock, DollarSign, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shop-level OptiAssets analytics. Manager-only.
 * Mounted at the top of /optiassets.
 */
export function OptiAssetsAnalyticsPanel() {
  const { isManager, isOwner } = usePermissions();
  const { data: summary, isLoading } = useShopAssetsSummary();
  const [collapsed, setCollapsed] = useState(false);

  if (!isManager && !isOwner) return null;

  const tiles = [
    {
      label: 'Total Assets',
      value: summary?.total_assets ?? 0,
      icon: Package,
      tone: 'default' as const,
    },
    {
      label: 'Currently Down',
      value: summary?.assets_down ?? 0,
      icon: PowerOff,
      tone: (summary?.assets_down ?? 0) > 0 ? ('danger' as const) : ('default' as const),
    },
    {
      label: 'Maint. Due (30d)',
      value: summary?.maintenance_due_30d ?? 0,
      icon: Wrench,
      tone: (summary?.maintenance_due_30d ?? 0) > 0 ? ('warning' as const) : ('default' as const),
    },
    {
      label: 'Downtime (MTD)',
      value: `${(summary?.downtime_hours_mtd ?? 0).toFixed(1)}h`,
      icon: Clock,
      tone: 'default' as const,
    },
    {
      label: 'Maint. Spend (MTD)',
      value: `$${Number(summary?.maintenance_spend_mtd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      icon: DollarSign,
      tone: 'default' as const,
    },
  ];

  return (
    <div className="border-b bg-muted/30">
      <div className="px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium text-muted-foreground">Shop Analytics</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </Button>
      </div>

      {!collapsed && (
        <div className="px-6 pb-4">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {tiles.map((t) => {
                const Icon = t.icon;
                return (
                  <Card
                    key={t.label}
                    className={cn(
                      t.tone === 'danger' && 'border-destructive/40 bg-destructive/5',
                      t.tone === 'warning' && 'border-amber-300/60 bg-amber-50/60 dark:border-amber-800/60 dark:bg-amber-950/20',
                    )}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">{t.label}</p>
                        <Icon
                          className={cn(
                            'h-4 w-4',
                            t.tone === 'danger' && 'text-destructive',
                            t.tone === 'warning' && 'text-amber-600 dark:text-amber-400',
                            t.tone === 'default' && 'text-muted-foreground',
                          )}
                        />
                      </div>
                      <p className="text-2xl font-semibold mt-1">{t.value}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
